import React from "react";
import { Area, AreaChart, CartesianGrid, LabelList, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp } from "lucide-react";

import { TOOLTIP_STYLE } from "../components/Estados";
import { formatCompacto, formatCurrency, formatInt } from "../lib/formato";

function Chip({ cor, rotulo, valor }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg border ${cor}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold">{rotulo}</span>{" "}
      <span className="text-sm font-mono font-bold tabular-nums">{valor}</span>
    </div>
  );
}

function Balao({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-lg">
      <p className="font-semibold">{d.data}</p>
      <p>{formatCurrency(d.valor)}</p>
      <p className="text-xs text-muted-foreground">{d.pedidos ? `${formatInt(d.pedidos)} pedido(s)` : "sem venda"}</p>
    </div>
  );
}

// Um ponto por dia do período até hoje — o dia sem venda aparece em R$ 0,00 e
// ENTRA na média (pedido do dono, 21/09/2026; antes o dia zerado sumia do
// gráfico e a média dividia só pelos dias com venda).
export default function FaturamentoDiario({ pontos }) {
  if (!pontos?.length) return null;
  const total = pontos.reduce((s, d) => s + (Number(d.valor) || 0), 0);
  const media = total / pontos.length;
  const semVenda = pontos.filter((d) => !Number(d.valor)).length;
  return (
    <div className="bg-card rounded-xl border p-6" data-e2e="faturamento-diario">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <TrendingUp className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-bold">Faturamento Diário</h2>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Chip cor="bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300" rotulo="Total" valor={formatCurrency(total)} />
          <Chip cor="bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300"
            rotulo={`Média/dia (${pontos.length} ${pontos.length === 1 ? "dia" : "dias"})`} valor={formatCurrency(media)} />
          {semVenda > 0 && (
            <Chip cor="bg-muted border-border text-muted-foreground" rotulo="Dias sem venda" valor={formatInt(semVenda)} />
          )}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <AreaChart data={pontos} margin={{ top: 30, right: 20, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradFaturamentoDiario" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0088FE" stopOpacity={0.6} />
              <stop offset="95%" stopColor="#0088FE" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" vertical={false} />
          <XAxis dataKey="data" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12 }} />
          <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
          <Tooltip content={<Balao />} contentStyle={TOOLTIP_STYLE} />
          <ReferenceLine y={media} stroke="#f59e0b" strokeDasharray="6 4" strokeWidth={2}
            label={{ value: `Média ${formatCurrency(media)}`, position: "insideTopRight", fill: "#f59e0b", fontSize: 11, fontWeight: 600 }} />
          <Area type="monotone" dataKey="valor" stroke="#0088FE" strokeWidth={3} fillOpacity={1} fill="url(#gradFaturamentoDiario)"
            dot={{ fill: "#0088FE", stroke: "#fff", strokeWidth: 2, r: 4 }} activeDot={{ fill: "#0088FE", stroke: "#fff", strokeWidth: 3, r: 6 }}>
            <LabelList dataKey="valor" position="top" formatter={formatCompacto} style={{ fontSize: 10, fill: "hsl(var(--foreground))", fontWeight: 600 }} />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
